import React from 'react';

import { Box } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAddMappedEntryToCart } from 'features/menu/components/product-wizard/use-add-to-cart';
import { useMinimalRecentEntryAvailability } from 'features/menu/pages/recent-items-page/minimal-recent-items/use-minimal-recent-entry-availability';
import { useStoreContext } from 'features/store';
import { LoadingIndicator } from 'features/store/pages/pickup-store-selector/components';

import ActionButton, { ActionButtonSizes, ActionButtonVariants } from 'components/action-button';
import { BlankScreen } from 'components/blank-screen';
import { CardModal } from 'components/card-modal';
import { toaster } from 'components/toaster/toaster';
import { ItemAvailabilityStatus } from 'enums/menu';
import { useRefundEligibility } from 'hooks/refund-eligibility';
import { useSupportData } from 'hooks/use-support-data';
import { LoyaltyLoader } from 'pages/loyalty/loyalty-loader';
import { CustomEventNames, logAddRecentItemToCartEvent, logNavigationClick } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { useMinimalAccountOrderDetails } from '../use-minimal-account-order-details';

import { Divider, NeedHelpButtonLoadingIndicatorWrapper } from './details-modal.styled';
import MinimalCartEntry from './minimal-cart-entry';
import MinimalReceiptDetails from './minimal-receipt-details';
import MinimalReceiptHeadInfo from './minimal-receipt-head-info';
import { PointsEarnedBlock } from './points-earned';
import { useHelpLinkUrlGenerator } from './use-help-link-url-generator';

export const MinimalDetailsModal: React.FC = () => {
  const router = useRouter();
  const { rbiOrderId } = useLocalSearchParams<{ rbiOrderId: string }>();
  const { formatCurrencyForLocale } = useUIContext();
  const { order, loading: isOrderLoading, refetch } = useMinimalAccountOrderDetails(rbiOrderId);
  const addMappedEntryToCart = useAddMappedEntryToCart();
  const { isStoreOpenAndAvailable, noStoreSelected, store } = useStoreContext();
  const { availability, hasSomeAvailableEntry } = useMinimalRecentEntryAvailability(
    order?.minimalEntries ?? []
  );

  const { isDeliveryActive } = useRefundEligibility({
    orderId: rbiOrderId,
  });
  const { isSupportDataLoading } = useSupportData();
  const helpLink = useHelpLinkUrlGenerator(rbiOrderId);
  const loyaltyTransaction = order?.loyaltyTransaction;

  const handleNeedHelpClick = () => {
    logNavigationClick(CustomEventNames.BUTTON_CLICK_RECENT_ORDER_NEED_HELP);
    router.navigate(helpLink);
  };

  const handleReorderClick = () => {
    if (!order || !isStoreOpenAndAvailable || noStoreSelected) {
      return;
    }

    const availableEntries = order.minimalEntries.filter(
      (_, index) => availability[index]?.itemAvailabilityStatus === ItemAvailabilityStatus.AVAILABLE
    );

    const addedEntries = availableEntries.filter(entry => {
      const added = addMappedEntryToCart(entry);
      if (added) {
        logAddRecentItemToCartEvent(entry?.name ?? '', store);
      }
      return added;
    });

    const [firstEntry] = addedEntries;
    if (firstEntry) {
      const message =
        addedEntries.length === 1
          ? firstEntry.quantity > 1
            ? t('addToCartMultipleSuccess', {
                itemName: firstEntry.name,
                quantity: firstEntry.quantity - 1,
              })
            : t('addToCartSuccess', { itemName: firstEntry.name })
          : t('cartUpdatedRecentReorder', 'Your cart was updated');

      toaster.addToCartToast(message, firstEntry.image);
    }

    if (addedEntries.length) {
      router.replace(routes.cart);
    }
  };

  return (
    <CardModal
      standardPadding
      analyticsEvent="Order Details"
      headerText={t('receipt')}
      scrollView
      footer={
        <>
          {isDeliveryActive && order ? (
            <ActionButton
              fullWidth
              onPress={() =>
                router.navigate(`${routes.orderConfirmation}/${order.rbiOrderId}?dismissOnly=true`)
              }
              isLoading={isOrderLoading}
            >
              {t('trackOrder')}
            </ActionButton>
          ) : (
            <ActionButton
              fullWidth
              onPress={handleReorderClick}
              isLoading={isOrderLoading}
              disabled={!hasSomeAvailableEntry || !isStoreOpenAndAvailable || noStoreSelected}
            >
              {t('reorder')}
            </ActionButton>
          )}
          <Box paddingY="$4" focusable>
            {t('pricesDiscountsAndAvailabilityAreSbujectToChangeWhenYouReorder')}
          </Box>

          {isSupportDataLoading && (
            <NeedHelpButtonLoadingIndicatorWrapper>
              <LoadingIndicator />
            </NeedHelpButtonLoadingIndicatorWrapper>
          )}

          {!isSupportDataLoading && (
            <ActionButton
              testID="recent-order-need-help"
              fullWidth
              variant={ActionButtonVariants.OUTLINE}
              size={ActionButtonSizes.SMALL}
              onPress={handleNeedHelpClick}
            >
              {t('needHelp')}
            </ActionButton>
          )}
        </>
      }
    >
      {!isOrderLoading && order ? (
        <>
          <Box>
            <MinimalReceiptHeadInfo order={order} />
            <Divider />
          </Box>
          {loyaltyTransaction && (
            <>
              <PointsEarnedBlock loyaltyTransaction={loyaltyTransaction} />
              <Divider />
            </>
          )}
          <Box>
            {order.minimalEntries.map((entry, i) => (
              <React.Fragment key={entry.name + i}>
                <MinimalCartEntry currencyFormatter={formatCurrencyForLocale} entry={entry} />
                <Divider />
              </React.Fragment>
            ))}
          </Box>
          <Box>
            <MinimalReceiptDetails order={order} />
          </Box>
        </>
      ) : isOrderLoading ? (
        <LoyaltyLoader />
      ) : (
        <BlankScreen context={{ reason: 'there is not rbiOrderId' }} onReload={refetch} />
      )}
    </CardModal>
  );
};
